// Pure layout + presentation helpers for the swimlane trace view.
//
// The interpreter emits a flat list of trace events; this module turns that
// list into (a) the five text columns each event fills, and (b) a swimlane
// geometry — vertical lifelines for threads / transactions / keys, grouped into
// three bands and reused once an object dies. React components consume the
// results; nothing here touches the DOM.

import type { TraceEvent } from "../src/backend/interp/trace.js";
import type { Address } from "../src/backend/interp/value.js";

/** A map from object address to its type name (`Store.objectTypes()`). */
export type ObjectTypes = Map<Address, string>;

/**
 * Type names for every key address the trace touches. Starts from the store's
 * committed types and fills any gaps from the trace's own write events: an
 * object created only inside a transaction that later *aborted* never lands in
 * the store, but its write still rendered as `Point{...}` / `Set<...>`, so the
 * leading identifier recovers its type. Keeps aborted-attempt objects (which the
 * game example produces on every retry) from falling into an untyped sub-band.
 */
export function resolveTypes(events: TraceEvent[], storeTypes?: ObjectTypes): ObjectTypes {
  const out = new Map(storeTypes ?? []);
  for (const ev of events) {
    // Only top-level objects ("/N") carry a type name; entries resolve via their
    // parent collection, which is itself a top-level object.
    if (ev.type !== "write" || out.has(ev.addr) || ev.addr.split("/").length !== 2) continue;
    const m = /^([A-Za-z_][A-Za-z0-9_]*)[<{]/.exec(ev.value);
    if (m) out.set(ev.addr, m[1]!);
  }
  return out;
}

/** The lowercased type of the top-level object an address lives under, or null
 *  when it (or its type) is unknown. `/3/o2` (a Set entry) resolves via `/3`. */
function keyType(addr: Address, types?: ObjectTypes): string | null {
  if (!types) return null;
  const top = "/" + (addr.split("/")[1] ?? "");
  const t = types.get(top);
  return t ? t.toLowerCase() : null;
}

/** An address with its object's type spliced in after the leading slash, so a
 *  key reads as `/player/2` (or `/set/3/o2` for a collection entry) instead of a
 *  bare `/2`. Falls back to the raw address when the type is unknown. */
export function describeKey(addr: Address, types?: ObjectTypes): string {
  const t = keyType(addr, types);
  return t ? `/${t}${addr}` : addr;
}

export function eventThread(ev: TraceEvent): number {
  if (ev.type === "spawn") return ev.parent;
  return (ev as { thread: number }).thread;
}

/** Colour of an event's horizontal connector — matches its `op` text colour so
 *  the two read as one thing. Theme vars keep read/write/print in sync with the
 *  editor foreground. */
export function opColor(ev: TraceEvent): string {
  switch (ev.type) {
    case "read":
      return "var(--muted)";
    case "write":
    case "print":
      return "var(--fg)";
    case "assert":
      // Green when the assertion held, red when it did not — so a failed outcome
      // is visible at a glance in the trace, the same way a commit/abort reads.
      return ev.passed ? "var(--op-commit)" : "var(--op-abort)";
    case "txn-begin":
      return "var(--op-txn)";
    case "txn-commit":
      return "var(--op-commit)";
    case "txn-abort":
    case "txn-retry":
    case "orelse-retry":
      return "var(--op-abort)";
    case "spawn":
      return "var(--op-spawn)";
    case "thread-start":
    case "thread-end":
      return "var(--op-thread)";
    case "sleep":
      return "var(--op-sleep)";
    case "lock-acquire":
    case "lock-release":
      return "var(--op-lock)";
    case "lock-wait":
      return "var(--op-wait)";
  }
}

function txnLabel(txn: number | "auto"): string {
  return txn === "auto" ? "auto" : `#${txn}`;
}

/** The transaction id an event belongs to, or null for auto / non-txn events.
 *  Covers every event that shows a `#N` in the trace's txn column — including the
 *  2PL lock ops, which bracket the txn's begin/commit — so the txn lifeline can be
 *  made to span exactly the rows that name it. */
export function eventTxnId(ev: TraceEvent): number | null {
  switch (ev.type) {
    case "txn-begin":
    case "txn-commit":
    case "txn-abort":
    case "txn-retry":
    case "orelse-retry":
    case "lock-acquire":
    case "lock-wait":
    case "lock-release":
      return ev.txn;
    case "read":
    case "write":
      return ev.txn === "auto" ? null : ev.txn;
    default:
      return null;
  }
}

/** Map each transaction id to how it ended, so a row can show its fate inline. */
export function txnOutcomes(events: TraceEvent[]): Map<number, "commit" | "abort"> {
  const out = new Map<number, "commit" | "abort">();
  for (const ev of events) {
    if (ev.type === "txn-commit") out.set(ev.txn, "commit");
    else if (ev.type === "txn-abort") out.set(ev.txn, "abort");
  }
  return out;
}

export interface EventColumns {
  op: string;
  opCls: string;
  txn: string;
  key: string;
  value: string;
}

/** The five right-hand columns for one event: op, txn id, key, value. When
 *  `types` is given, key addresses gain their object's type (`/player/2`). */
export function eventColumns(ev: TraceEvent, types?: ObjectTypes): EventColumns {
  const addrs = (as: Address[]) => as.map((a) => describeKey(a, types)).join(", ");
  switch (ev.type) {
    case "spawn":
      return { op: "spawn", opCls: "op-spawn", txn: "", key: "", value: `→ T${ev.child} ${ev.fn}()` };
    case "thread-start":
      return { op: "start", opCls: "op-thread", txn: "", key: "", value: `${ev.fn}()` };
    case "thread-end":
      return { op: "end", opCls: "op-thread", txn: "", key: "", value: `${ev.fn}()` };
    case "sleep":
      return { op: "sleep", opCls: "op-sleep", txn: "", key: "", value: `${ev.duration} → t=${ev.until.toFixed(2)}` };
    case "txn-begin":
      return { op: "begin", opCls: "op-txn", txn: `#${ev.txn}`, key: "", value: `readV=${ev.readVersion}` };
    case "txn-commit":
      return {
        op: "commit",
        opCls: "op-commit",
        txn: `#${ev.txn}`,
        key: "",
        value: `v${ev.commitVersion} · ${ev.reads}r/${ev.writes}w`,
      };
    case "txn-abort":
      return { op: "abort", opCls: "op-abort", txn: `#${ev.txn}`, key: "", value: `${ev.reason} · retry#${ev.attempt}` };
    case "txn-retry":
      return { op: "retry", opCls: "op-abort", txn: `#${ev.txn}`, key: "", value: `park · ${ev.reads}r` };
    case "orelse-retry":
      return { op: "orElse", opCls: "op-abort", txn: `#${ev.txn}`, key: "", value: "branch retried → next" };
    case "read":
      return { op: "read", opCls: "op-read", txn: txnLabel(ev.txn), key: describeKey(ev.addr, types), value: ev.value };
    case "write":
      return { op: "write", opCls: "op-write", txn: txnLabel(ev.txn), key: describeKey(ev.addr, types), value: ev.value };
    case "print":
      return { op: "print", opCls: "op-print", txn: "", key: "", value: ev.message };
    case "assert":
      return {
        op: "assert",
        opCls: ev.passed ? "op-commit" : "op-abort",
        txn: "",
        key: "",
        value: `${ev.passed ? "✓" : "✗"}${ev.message ? ` ${ev.message}` : ""}`,
      };
    case "lock-acquire":
      return { op: "lock", opCls: "op-lock", txn: `#${ev.txn}`, key: "", value: `hold {${addrs(ev.addrs)}}` };
    case "lock-wait":
      return { op: "wait", opCls: "op-wait", txn: `#${ev.txn}`, key: "", value: `blocked on {${addrs(ev.addrs)}}` };
    case "lock-release":
      return { op: "unlock", opCls: "op-lock", txn: `#${ev.txn}`, key: "", value: `free {${addrs(ev.addrs)}}` };
  }
}

// ---------------------------------------------------------------------------
// Swimlane geometry
// ---------------------------------------------------------------------------

export type LaneGroup = "thread" | "txn" | "key";

export interface Lane {
  group: LaneGroup; // colour comes from the group, not the individual lane
  subgroup?: string; // key lanes only: the object's type, its sub-band within "keys"
  col: number; // column index within the group/sub-band (reused across dead lanes)
  birth: number; // row index where the lane appears
  death: number; // row index where the lane dies, or -1 if it lives to the end
  label: string;
  outcome?: "commit" | "abort"; // txn lanes only: how the transaction ended
}

export interface RowInfo {
  parts: number[]; // lane indices touched by this event
  color: string; // connector colour, matching the event's op text
}

export interface LaneSeg {
  lane: number;
  isBirth: boolean;
  isDeath: boolean;
  // A thread lifeline is dotted while the thread is blocked: parked on a lock
  // between `lock-wait` and `lock-acquire`, asleep between `sleep` and its wake,
  // or parked on a txn's read set between a `retry` (`txn-retry`) and its re-run.
  // The two halves of a row's segment are marked independently so the dotting
  // starts/stops at the event circle (MID), not the row boundary.
  blockedTop?: boolean; // the 0→MID half (entering the row from above)
  blockedBottom?: boolean; // the MID→ROW_H half (leaving the row downward)
}

export const GROUP_ORDER: LaneGroup[] = ["thread", "txn", "key"];
export const GROUP_LABEL: Record<LaneGroup, string> = { thread: "threads", txn: "txns", key: "keys" };
export const ROW_H = 22;
export const MID = ROW_H / 2;
const LANE_GAP = 15;
const GROUP_GAP = 22;
const SUB_GAP = 12; // gap between key sub-bands (one per type), < GROUP_GAP
const LANE_PAD = 8; // padding from the outer lanes to the shaded band edge
const PAD = 12;
export const HEAD_H = 40; // two-level header: group labels over key sub-band labels

export interface Band {
  group: LaneGroup;
  subgroup?: string; // set for the per-type sub-bands within "keys"
  left: number;
  right: number;
}

/** One type's sub-band within the "keys" group: its lane span and shaded edges. */
export interface KeySubBand {
  type: string; // "" when types are unavailable (a single, unlabelled sub-band)
  x0: number; // first lane x
  x1: number; // last lane x
  left: number; // shaded band left edge
  right: number; // shaded band right edge
}

export interface Layout {
  lanes: Lane[];
  rows: RowInfo[];
  perRowLanes: LaneSeg[][];
  x: number[]; // per-lane x coordinate
  groupSpan: Partial<Record<LaneGroup, { x0: number; x1: number }>>;
  bands: Band[]; // one shaded rectangle per non-empty group (keys: one per type)
  keySubBands: KeySubBand[]; // per-type sub-bands, left→right, for the second header row
  width: number;
}

/**
 * Assign every thread / txn / key a lifeline column, reusing dead columns.
 * When `types` is supplied, key lifelines are further split into one sub-band
 * per object type (`/player/*` beside `/bullet/*`), each packing its own columns.
 */
export function layoutLanes(events: TraceEvent[], types?: ObjectTypes): Layout {
  const lanes: Lane[] = [];
  const rows: RowInfo[] = [];
  const lastRow = events.length - 1;

  const threadLane = new Map<number, number>();
  const txnLane = new Map<number, number>();
  const keyLane = new Map<string, number>();

  // Columns are packed per "pool" so dead columns are reused within, but never
  // across, a pool. Threads and txns are one pool each ("thread"/"txn"); every
  // key sub-band is its own pool ("key:<type>"), which is what keeps each type's
  // lifelines within its own band.
  const active = new Map<string, Set<number>>();
  const maxCols = new Map<string, number>();
  const poolKey = (group: LaneGroup, subgroup?: string): string =>
    group === "key" ? `key:${subgroup ?? ""}` : group;

  // Key sub-bands in first-appearance order, so they lay out left→right stably.
  const keySubOrder: string[] = [];
  const seenSub = new Set<string>();

  const allocCol = (pool: string): number => {
    let set = active.get(pool);
    if (!set) active.set(pool, (set = new Set()));
    let c = 0;
    while (set.has(c)) c++;
    set.add(c);
    maxCols.set(pool, Math.max(maxCols.get(pool) ?? 0, c + 1));
    return c;
  };
  const openLane = (group: LaneGroup, row: number, label: string, subgroup?: string): number => {
    const idx = lanes.length;
    lanes.push({ group, subgroup, col: allocCol(poolKey(group, subgroup)), birth: row, death: -1, label });
    return idx;
  };
  const closeLane = (idx: number, row: number): void => {
    const lane = lanes[idx]!;
    lane.death = row;
    active.get(poolKey(lane.group, lane.subgroup))!.delete(lane.col);
  };
  const ensureThread = (tid: number, row: number): number => {
    let idx = threadLane.get(tid);
    if (idx === undefined) {
      idx = openLane("thread", row, `T${tid}`);
      threadLane.set(tid, idx);
    }
    return idx;
  };
  const ensureKey = (addr: string, row: number): number => {
    let idx = keyLane.get(addr);
    if (idx === undefined) {
      const sub = keyType(addr, types) ?? "";
      if (!seenSub.has(sub)) {
        seenSub.add(sub);
        keySubOrder.push(sub);
      }
      idx = openLane("key", row, addr, sub);
      keyLane.set(addr, idx);
    }
    return idx;
  };
  const ensureTxn = (txnId: number, row: number): number => {
    let idx = txnLane.get(txnId);
    if (idx === undefined) {
      idx = openLane("txn", row, `#${txnId}`);
      txnLane.set(txnId, idx);
    }
    return idx;
  };

  // The last row that names each txn, so its lifeline can be closed there — for a
  // 2PL txn that is the `lock-release`, which follows the commit, not the commit.
  const txnLastRow = new Map<number, number>();
  // A txn abandoned by a `retry`: the row it parked on, and where its next attempt
  // (a fresh txn id, linked by `prevTxn`) takes over.
  const retryRow = new Map<number, number>();
  const successorBegin = new Map<number, number>();
  events.forEach((ev, row) => {
    const t = eventTxnId(ev);
    if (t !== null) txnLastRow.set(t, row);
    if (ev.type === "txn-retry") retryRow.set(ev.txn, row);
    if (ev.type === "txn-begin" && ev.prevTxn !== undefined) successorBegin.set(ev.prevTxn, row);
  });

  // Where each txn's lifeline ends. A `retry`-parked txn is *waiting*, not
  // finished, so its lane stays alive (dotted, below) past its retry row until the
  // attempt that replaces it begins — or to the end of the trace if it never wakes.
  const closeAt = new Map<number, number[]>();
  for (const [t, last] of txnLastRow) {
    const close = retryRow.has(t) ? successorBegin.get(t) : last;
    if (close === undefined) continue; // parked and never woken: lives to the end
    const at = closeAt.get(close);
    if (at) at.push(t);
    else closeAt.set(close, [t]);
  }

  // Rows on which each thread acted, in order — used to find the spans where a
  // thread is blocked (a `sleep`/`lock-wait` up to that thread's next event).
  const threadRows = new Map<number, number[]>();

  events.forEach((ev, row) => {
    const tid = eventThread(ev);
    const parts: number[] = [ensureThread(tid, row)];
    (threadRows.get(tid) ?? threadRows.set(tid, []).get(tid)!).push(row);
    let closeThread = -1;

    // Every event that names a txn (begin/commit/abort/retry, a non-auto
    // read/write, and the 2PL lock ops) is a participant on that txn's lifeline,
    // which opens on the first such row — the `lock-wait`/`lock-acquire` for a 2PL
    // txn, well before `begin`. This keeps the invariant that a `#N` in the txn
    // column always has a lifeline under it.
    const txnId = eventTxnId(ev);
    if (txnId !== null) parts.push(ensureTxn(txnId, row));

    // A re-run also touches the attempt it replaces, so the row draws a connector
    // from the parked (or aborted) txn's lifeline to its successor's — the handoff.
    if (ev.type === "txn-begin" && ev.prevTxn !== undefined) {
      const prev = txnLane.get(ev.prevTxn);
      if (prev !== undefined) parts.push(prev);
    }

    switch (ev.type) {
      case "spawn":
        parts.push(ensureThread(ev.child, row));
        break;
      case "thread-end":
        closeThread = threadLane.get(tid)!;
        break;
      case "txn-commit":
      case "txn-abort": {
        const idx = txnLane.get(ev.txn);
        if (idx !== undefined) lanes[idx]!.outcome = ev.type === "txn-commit" ? "commit" : "abort";
        break;
      }
      case "read":
      case "write":
        parts.push(ensureKey(ev.addr, row));
        break;
      case "lock-acquire":
      case "lock-wait":
      case "lock-release":
        for (const a of ev.addrs) parts.push(ensureKey(a, row));
        break;
    }

    rows.push({ parts, color: opColor(ev) });
    // Close any txn lanes that end here — the last row naming the txn (the
    // `lock-release` for a 2PL txn, else the commit/abort), or, for a parked txn,
    // the row its successor begins — freeing the column for reuse.
    for (const t of closeAt.get(row) ?? []) {
      const idx = txnLane.get(t);
      if (idx !== undefined) closeLane(idx, row);
    }
    if (closeThread >= 0) closeLane(closeThread, row);
  });

  // Column geometry: the three groups laid out left to right. Threads and txns
  // are single bands; "keys" is subdivided into one sub-band per object type.
  const groupX0: Record<LaneGroup, number> = { thread: 0, txn: 0, key: 0 };
  const groupSpan: Layout["groupSpan"] = {};
  const subX0 = new Map<string, number>(); // key sub-band type → first-lane x
  const keySubBands: KeySubBand[] = [];
  let cursor = PAD;

  for (const g of ["thread", "txn"] as const) {
    groupX0[g] = cursor;
    const cols = maxCols.get(g) ?? 0;
    if (cols > 0) {
      groupSpan[g] = { x0: cursor, x1: cursor + (cols - 1) * LANE_GAP };
      cursor += (cols - 1) * LANE_GAP + LANE_GAP + GROUP_GAP;
    }
  }

  groupX0.key = cursor;
  if (keySubOrder.length > 0) {
    for (const sub of keySubOrder) {
      const cols = maxCols.get(poolKey("key", sub)) ?? 0;
      const x0 = cursor;
      const x1 = x0 + Math.max(cols - 1, 0) * LANE_GAP;
      subX0.set(sub, x0);
      keySubBands.push({ type: sub, x0, x1, left: x0 - LANE_PAD, right: x1 + LANE_PAD });
      cursor = x1 + LANE_GAP + SUB_GAP;
    }
    cursor -= SUB_GAP; // no trailing gap after the last sub-band
    const first = keySubBands[0]!;
    const last = keySubBands[keySubBands.length - 1]!;
    groupSpan.key = { x0: first.x0, x1: last.x1 };
    cursor += GROUP_GAP; // mirror the per-group trailing gap for the width formula
  }
  const width = Math.max(cursor - GROUP_GAP + PAD, PAD * 2);

  const bands: Band[] = [];
  for (const g of ["thread", "txn"] as const) {
    const span = groupSpan[g];
    if (span) bands.push({ group: g, left: span.x0 - LANE_PAD, right: span.x1 + LANE_PAD });
  }
  for (const sb of keySubBands) {
    bands.push({ group: "key", subgroup: sb.type, left: sb.left, right: sb.right });
  }

  const x = lanes.map((lane) =>
    lane.group === "key"
      ? (subX0.get(lane.subgroup ?? "") ?? groupX0.key) + lane.col * LANE_GAP
      : groupX0[lane.group] + lane.col * LANE_GAP
  );

  // Blocked spans per thread lane: [start, end] row pairs where `start` parks
  // the thread — asleep (`sleep`), waiting on a lock (`lock-wait`), or parked on
  // a txn's read set after a `retry` (`txn-retry`) — and `end` is that thread's
  // next event (its wake / acquire / re-run), or its death / the last row if it
  // never resumes. A spawned thread is also "not running yet" between the parent's
  // `spawn` (where its lane is born) and its own `thread-start`, so that leading
  // gap is a blocked span too.
  const blockedSpans = new Map<number, Array<[number, number]>>();
  for (const [tid, actRows] of threadRows) {
    const laneIdx = threadLane.get(tid);
    if (laneIdx === undefined) continue;
    const lane = lanes[laneIdx]!;
    const laneEnd = lane.death < 0 ? lastRow : lane.death;
    const spans: Array<[number, number]> = [];
    // Spawned-but-not-started: the lane is born at the `spawn` row but the thread
    // doesn't act until its `thread-start`, so it isn't running in between.
    const firstAct = actRows[0];
    if (firstAct !== undefined && lane.birth < firstAct) spans.push([lane.birth, firstAct]);
    for (let k = 0; k < actRows.length; k++) {
      const t = events[actRows[k]!]!.type;
      if (t === "sleep" || t === "lock-wait" || t === "txn-retry") {
        spans.push([actRows[k]!, k + 1 < actRows.length ? actRows[k + 1]! : laneEnd]);
      }
    }
    if (spans.length) blockedSpans.set(laneIdx, spans);
  }

  // A `retry`-parked txn's own lifeline is dotted from the row it parked on to the
  // row its next attempt takes over (or the end) — it still exists, waiting for a
  // committed write to its read set, but it isn't running.
  for (const [t, rrow] of retryRow) {
    const laneIdx = txnLane.get(t);
    if (laneIdx === undefined) continue;
    const lane = lanes[laneIdx]!;
    const spans = blockedSpans.get(laneIdx) ?? [];
    spans.push([rrow, lane.death < 0 ? lastRow : lane.death]);
    blockedSpans.set(laneIdx, spans);
  }

  const perRowLanes: LaneSeg[][] = events.map(() => []);
  lanes.forEach((lane, idx) => {
    const end = lane.death < 0 ? lastRow : lane.death;
    const spans = blockedSpans.get(idx);
    for (let r = lane.birth; r <= end; r++) {
      let blockedTop = false;
      let blockedBottom = false;
      if (spans) {
        for (const [s, e] of spans) {
          if (s < r && r <= e) blockedTop = true; // arriving at row r from a blocked span
          if (s <= r && r < e) blockedBottom = true; // leaving row r into a blocked span
        }
      }
      perRowLanes[r]!.push({
        lane: idx,
        isBirth: r === lane.birth,
        isDeath: lane.death >= 0 && r === lane.death,
        blockedTop,
        blockedBottom,
      });
    }
  });

  return { lanes, rows, perRowLanes, x, groupSpan, bands, keySubBands, width };
}
